/**
 * QdsHorizontalMenu Web Component
 * 
 * A horizontal navigation menu with dropdown submenus.
 * Displays top-level items in a horizontal layout and shows children as dropdowns on hover.
 * 
 * @element qds-horizontal-menu
 * 
 * @attr {String} data - JSON string containing menu items array
 * @attr {String} icon-root - Base URL for relative icon paths
 * @attr {Boolean} use-icons - Whether to display icons next to menu items (defaults to false)
 * 
 * @csspart nav-container - The main container for the horizontal navigation
 * @csspart top-level-item - Top level menu items
 * @csspart dropdown - Dropdown container for submenu items
 * 
 * @fires itemSelected - When a menu item is selected
 */
import { QdsBaseMenu } from '../base-menu/qds-base-menu.js';
import { adoptPrimeIconsStyles, loadBaseCSS } from '../shared/button-utils.js';
import { hydrateQdsLucideIcons } from '../shared/icon-utils.js';
import { HierarchicalMenuItem } from '../../types/index.js';

loadBaseCSS();

interface ItemSelectedEventDetail {
    url: string;
    text: string;
}

class QdsHorizontalMenu extends HTMLElement {
    private _baseMenu: QdsBaseMenu;

    /**
     * Creates an instance of QdsHorizontalMenu
     */
    constructor() {
        super();
        
        // Create the base menu instance
        this._baseMenu = new QdsBaseMenu();
        
        // Create shadow DOM
        this.attachShadow({ mode: "open" });
        
        // Initial render
        this.render();
    }

    static get observedAttributes(): string[] {
        return ["data", "icon-root", "use-icons"];
    }

    /**
     * Called when an observed attribute changes
     * 
     * @param name - Name of the attribute that changed
     * @param oldValue - Previous value of the attribute
     * @param newValue - New value of the attribute
     */
    attributeChangedCallback(name: string, oldValue: string, newValue: string): void {
        if (name === "data") {
            try {
                const data = JSON.parse(newValue);
                this._baseMenu.setData(data);
                this.render();
                // Re-evaluate active item when menu data changes
                this._setActiveItemFromURL();
            } catch (e) {
                console.error("Invalid JSON data:", e);
            }
        }

        if (name === "icon-root") {
            this._baseMenu.setIconRoot(newValue);
            this.render();
        }

        if (name === "use-icons") {
            // Check if the attribute is explicitly set to 'false'
            this._baseMenu.useIcons = !(newValue === 'false');
            this.render();
        }
    }

    /**
     * Called when the element is inserted into the DOM
     */
    connectedCallback(): void {
        if (!this.isConnected) return;
        
        // Add keyboard navigation support
        if (this.shadowRoot) {
            this.shadowRoot.addEventListener('keydown', this._handleKeydown.bind(this) as EventListener);
        }

        // Set active item based on current URL
        this._setActiveItemFromURL();
    }
    
    /**
     * Called when the element is removed from the DOM
     */
    disconnectedCallback(): void {
        // Remove keyboard listener
        if (this.shadowRoot) {
            this.shadowRoot.removeEventListener('keydown', this._handleKeydown.bind(this) as EventListener);
        }
    }
    
    /**
     * Handle keyboard navigation
     * @param event - Keyboard event
     */
    private _handleKeydown(event: KeyboardEvent): void {
        if (!this.shadowRoot) return;
        
        const activeElement = this.shadowRoot.activeElement as HTMLElement;
        
        if (!activeElement) return;
        
        // Top-level items
        if (activeElement.classList.contains('top-level-item')) {
            const topLevelItems = this.shadowRoot.querySelectorAll<HTMLElement>('.top-level-item');
            const currentIndex = Array.from(topLevelItems).indexOf(activeElement);
            
            if (event.key === 'ArrowRight') {
                const nextIndex = (currentIndex + 1) % topLevelItems.length;
                topLevelItems[nextIndex].focus();
                event.preventDefault();
            } else if (event.key === 'ArrowLeft') {
                const nextIndex = (currentIndex - 1 + topLevelItems.length) % topLevelItems.length;
                topLevelItems[nextIndex].focus();
                event.preventDefault();
            } else if (event.key === 'ArrowDown') {
                const dropdown = activeElement.nextElementSibling as HTMLElement;
                if (dropdown && dropdown.classList.contains('dropdown')) {
                    const firstDropdownItem = dropdown.querySelector<HTMLElement>('a');
                    if (firstDropdownItem) {
                        firstDropdownItem.focus();
                        event.preventDefault();
                    }
                }
            }
        }
        // Dropdown items
        else if (activeElement.closest('.dropdown, .submenu, .flyout-menu')) {
            const container = activeElement.closest<HTMLElement>('.dropdown, .submenu, .flyout-menu');
            if (!container) return;
            
            const items = container.querySelectorAll<HTMLElement>('a:not(.submenu-trigger):not(.flyout-trigger)');
            const triggers = container.querySelectorAll<HTMLElement>('.submenu-trigger, .flyout-trigger');
            const allFocusable = [...Array.from(items), ...Array.from(triggers)];
            const currentIndex = allFocusable.indexOf(activeElement);
            
            if (event.key === 'ArrowDown') {
                const nextIndex = (currentIndex + 1) % allFocusable.length;
                allFocusable[nextIndex].focus();
                event.preventDefault();
            } else if (event.key === 'ArrowUp') {
                if (currentIndex === 0) {
                    // If at the first item of a dropdown, move back to parent item
                    if (container.classList.contains('dropdown')) {
                        const navItem = container.closest('.nav-item');
                        if (navItem) {
                            const topLevelItem = navItem.querySelector<HTMLElement>('.top-level-item');
                            if (topLevelItem) {
                                topLevelItem.focus();
                            }
                        }
                    } else {
                        const parentTrigger = container.parentElement?.querySelector<HTMLElement>('.submenu-trigger, .flyout-trigger');
                        if (parentTrigger) {
                            parentTrigger.focus();
                        }
                    }
                } else {
                    const nextIndex = (currentIndex - 1) % allFocusable.length;
                    allFocusable[nextIndex].focus();
                }
                event.preventDefault();
            } else if (event.key === 'ArrowRight') {
                // If this is a submenu trigger, open the submenu and focus first item
                if (activeElement.classList.contains('submenu-trigger') || activeElement.classList.contains('flyout-trigger')) {
                    const submenu = activeElement.nextElementSibling as HTMLElement;
                    if (submenu) {
                        const firstItem = submenu.querySelector<HTMLElement>('a');
                        if (firstItem) {
                            firstItem.focus();
                            event.preventDefault();
                        }
                    }
                }
            } else if (event.key === 'ArrowLeft') {
                // If in a flyout menu, move back to parent menu item
                if (container.classList.contains('flyout-menu') || container.classList.contains('submenu')) {
                    const parentTrigger = container.parentElement?.querySelector<HTMLElement>('.submenu-trigger, .flyout-trigger');
                    if (parentTrigger) {
                        parentTrigger.focus();
                        event.preventDefault();
                    }
                }
            } else if (event.key === 'Escape') {
                // Close dropdown/submenu and focus on parent
                if (container.classList.contains('dropdown')) {
                    const navItem = container.closest('.nav-item');
                    if (navItem) {
                        const topLevelItem = navItem.querySelector<HTMLElement>('.top-level-item');
                        if (topLevelItem) {
                            topLevelItem.focus();
                        }
                    }
                } else {
                    const parentTrigger = container.parentElement?.querySelector<HTMLElement>('.submenu-trigger, .flyout-trigger');
                    if (parentTrigger) {
                        parentTrigger.focus();
                    }
                }
                event.preventDefault();
            }
        }
    }
    
    /**
     * Handle item click event
     * @param event - Click event
     */
    private _handleItemClick(event: Event): void {
        const target = (event.target as HTMLElement).closest('a');
        if (!target) return;
        
        // Skip default handling for submenu/flyout triggers
        if (target.classList.contains('submenu-trigger') || target.classList.contains('flyout-trigger')) {
            event.preventDefault();
            return;
        }
        
        // Prevent default for top-level items with dropdowns (href="#")
        if (target.classList.contains('top-level-item') && target.getAttribute('href') === '#') {
            event.preventDefault();
        }
        
        // Remove active class from all top-level items
        if (this.shadowRoot) {
            const allTopLevelItems = this.shadowRoot.querySelectorAll<HTMLElement>('.top-level-item');
            allTopLevelItems.forEach(item => item.classList.remove('active'));
        }
        
        // Add active class to the clicked item if it's a top-level item
        if (target.classList.contains('top-level-item')) {
            target.classList.add('active');
        }
        
        // For regular links, dispatch the custom event but don't prevent the default behavior
        // This allows the browser to handle the link click normally according to target attribute
        this.dispatchEvent(new CustomEvent<ItemSelectedEventDetail>('itemSelected', { 
            detail: { 
                url: target.getAttribute('href') || '',
                text: target.textContent?.trim() || ''
            },
            bubbles: true,
            composed: true
        }));
        
        // We explicitly do NOT call event.preventDefault() here for regular links
        // This allows the browser to handle the link normally and respect the target attribute
    }

    /**
     * Get whether icons are displayed
     * @returns True if icons are displayed, false otherwise
     */
    get useIcons(): boolean {
        return this._baseMenu.useIcons;
    }
    
    /**
     * Set whether icons should be displayed
     * @param value - True to display icons, false to hide them
     */
    set useIcons(value: boolean) {
        if (value) {
            this.setAttribute('use-icons', '');
        } else {
            this.setAttribute('use-icons', 'false');
        }
    }
    
    /**
     * Set the active menu item based on the current URL
     * @private
     */
    private _setActiveItemFromURL(): void {
        if (!this.shadowRoot) return;

        const menuItems = this._baseMenu.menuData;
        
        if (!menuItems || menuItems.length === 0) return;

        const currentPath = window.location.pathname;

        // Remove active class from all top-level items first
        const allTopLevelItems = this.shadowRoot.querySelectorAll<HTMLElement>('.top-level-item');
        allTopLevelItems.forEach(item => item.classList.remove('active'));

        // Find the matching menu item
        for (let i = 0; i < menuItems.length; i++) {
            const item = menuItems[i];
            
            // Check if this top-level item matches
            if (this._urlMatches(item.linkUrl, currentPath)) {
                // Find the corresponding DOM element and activate it
                const topLevelItems = this.shadowRoot.querySelectorAll<HTMLElement>('.top-level-item');
                if (topLevelItems[i]) {
                    topLevelItems[i].classList.add('active');
                }
                return;
            }
            
            // Check children if this item has them
            if (item.items && item.items.length > 0) {
                for (const child of item.items) {
                    if (this._urlMatches(child.linkUrl, currentPath)) {
                        // Activate the parent item
                        const topLevelItems = this.shadowRoot.querySelectorAll<HTMLElement>('.top-level-item');
                        if (topLevelItems[i]) {
                            topLevelItems[i].classList.add('active');
                        }
                        return;
                    }
                }
            }
        }
    }

    /**
     * Check if a URL matches the current path
     * @param itemUrl - The URL from the menu item
     * @param currentPath - The current pathname
     * @returns True if the URLs match
     * @private
     */
    private _urlMatches(itemUrl: string, currentPath: string): boolean {
        if (!itemUrl) return false;
        
        // Handle relative URLs by comparing just the pathname
        try {
            const itemPath = new URL(itemUrl, window.location.origin).pathname;
            return itemPath === currentPath;
        } catch (e) {
            // If URL parsing fails, do a simple string comparison
            return itemUrl === currentPath || itemUrl === window.location.href;
        }
    }
    
    /**
     * Create horizontal menu HTML from the menu data
     * @returns HTML string for the horizontal menu
     */
    private createHorizontalMenu(): string {
        // Get the menu data
        const menuItems = this._baseMenu.menuData;
        if (!menuItems || menuItems.length === 0) {
            return '';
        }
        
        /**
         * Generate HTML for submenu items
         * @param items - Items to render in the dropdown
         * @param level - Current nesting level (0-based)
         * @returns HTML for dropdown items
         */
        const createDropdownItems = (items: HierarchicalMenuItem[], level = 1): string => {
            return items.map(item => {
                // Check if this item has children
                if (this._baseMenu.hasChildren(item)) {
                    // For level 1+ we create submenu triggers
                    const itemClass = 'has-submenu';
                    
                    return `
                        <li class="${itemClass}">
                            <a href="#" class="submenu-trigger" aria-haspopup="true" aria-expanded="false">
                                ${item.name}
                                <span class="submenu-arrow">›</span>
                            </a>
                            <div class="submenu level-${level}">
                                <ul>
                                    ${createDropdownItems(item.items || [], level + 1)}
                                </ul>
                            </div>
                        </li>
                    `;
                } else {
                    // This is a leaf item with no children
                    return `
                        <li>
                            ${this._baseMenu.generateLinkHtml(item, {
                                className: 'menu-link',
                                includeIcon: this._baseMenu.useIcons
                            })}
                        </li>
                    `;
                }
            }).join('');
        };
        
        // Create top-level items with dropdowns
        const menuItemsHtml = menuItems.map(item => {
            // Check if this is a direct link or has children
            if (!this._baseMenu.hasChildren(item)) {
                // Direct link with no dropdown
                return `
                    <li class="nav-item">
                        <a href="${item.linkUrl}" class="top-level-item" part="top-level-item" title="${item.description || ''}" target="${item.target || '_self'}">
                            ${this._baseMenu.useIcons && item.icon ? this._baseMenu.generateIconHtml(item) : ''}
                            ${item.name}
                        </a>
                    </li>
                `;
            }
            
            // Item with dropdown children
            return `
                <li class="nav-item">
                    <a href="#" class="top-level-item" part="top-level-item" 
                       tabindex="0" aria-haspopup="true" aria-expanded="false">
                        ${item.name}
                    </a>
                    <div class="dropdown" part="dropdown">
                        <ul>
                            ${createDropdownItems(item.items || [])}
                        </ul>
                    </div>
                </li>
            `;
        }).join('');
        
        return `
            <nav class="horizontal-nav" part="nav-container">
                <ul class="nav-list">
                    ${menuItemsHtml}
                </ul>
            </nav>
        `;
    }
    
    /**
     * Renders the component's HTML structure and attaches event listeners
     */
    private render(): void {
        if (!this.shadowRoot) return;
        
        this.shadowRoot.innerHTML = `
            <style>
                :host {
                    --_menu-text-color: var(--menu-text-color, var(--theme-text-primary));
                    --_menu-surface-color: var(--menu-background, var(--theme-background-primary));
                    --_menu-elevated-surface-color: var(--menu-elevated-background, var(--menu-background, var(--theme-background-primary)));
                    --_menu-hover-color: var(--menu-hover-background, var(--theme-background-tertiary));
                    --_menu-border-color: var(--menu-border-color, var(--theme-border-primary));
                    --_menu-muted-text-color: var(--menu-muted-text-color, var(--theme-text-tertiary));
                    --_menu-shadow: var(--menu-shadow, var(--theme-shadow-sm));
                    --_menu-active-indicator-color: var(--menu-active-indicator-color, var(--theme-success, var(--brand-green)));
                    display: block;
                    font-family: var(--qds-font-family, 'IBM Plex Sans', sans-serif);
                    color: var(--_menu-text-color);
                    position: relative;
                    z-index: 100; /* Higher z-index to ensure menus appear above page content */
                }
                
                *, *::before, *::after {
                    box-sizing: border-box;
                }
                
                .horizontal-nav {
                    background-color: var(--_menu-surface-color);
                }
                
                .nav-list {
                    list-style: none;
                    margin: 0;
                    padding: 0;
                    display: flex;
                    flex-wrap: nowrap;
                }
                
                .nav-item {
                    position: relative;
                    display: flex;
                    flex-direction: column;
                }
                
                .top-level-item {
                    display: flex;
                    align-items: center;
                    padding: 15px 20px;
                    color: var(--_menu-text-color);
                    text-decoration: none;
                    font-weight: 500;
                    transition: background-color 0.2s, border-bottom 0.2s;
                    white-space: nowrap;
                    cursor: pointer;
                    border-bottom: 2px solid transparent;
                }
                
                .top-level-item:hover, .top-level-item:focus {
                    background-color: var(--_menu-hover-color);
                    outline: none;
                }
                
                .top-level-item.active {
                    border-bottom-color: var(--_menu-active-indicator-color);
                }
                
                .dropdown {
                    position: absolute;
                    top: 100%;
                    left: 0;
                    min-width: 220px;
                    background-color: var(--_menu-elevated-surface-color);
                    box-shadow: var(--_menu-shadow);
                    border-radius: 0 0 4px 4px;
                    opacity: 0;
                    visibility: hidden;
                    transition: opacity 0.2s, visibility 0.2s;
                    z-index: 1000;
                    border: 1px solid var(--_menu-border-color);
                    pointer-events: none; /* Fix for hover issues */
                    overflow: visible !important; /* Prevent clipping from parent containers */
                    transform: translateZ(0); /* Create stacking context for better z-index handling */
                }
                
                .nav-item:hover .dropdown, 
                .top-level-item:focus + .dropdown,
                .dropdown:hover {
                    opacity: 1;
                    visibility: visible;
                    pointer-events: auto; /* Re-enable pointer events on hover */
                    display: block; /* Ensure it's visible regardless of opacity transitions */
                }
                
                /* Add a small buffer zone to make hovering easier */
                .dropdown::before {
                    content: '';
                    position: absolute;
                    top: -10px;
                    left: 0;
                    width: 100%;
                    height: 10px;
                    background: transparent;
                }
                
                .dropdown ul {
                    list-style: none;
                    margin: 0;
                    padding: 8px 0;
                }
                
                .dropdown li {
                    margin: 0;
                }
                
                .dropdown a {
                    display: flex;
                    align-items: center;
                    padding: 10px 20px;
                    color: var(--_menu-text-color);
                    text-decoration: none;
                    transition: background-color 0.1s;
                }
                
                .dropdown a:hover {
                    background-color: var(--_menu-hover-color);
                }
                
                /* Submenu and flyout menu styles */
                .has-submenu, .has-flyout {
                    position: relative;
                }
                
                .submenu-trigger {
                    display: flex;
                    justify-content: space-between;
                    padding-right: 30px !important;
                }
                
                .submenu-arrow {
                    margin-left: 8px;
                    font-size: 12px;
                    color: var(--_menu-muted-text-color);
                }
                
                /* Submenus within dropdowns */
                .submenu {
                    position: absolute;
                    top: 0;
                    left: 100%;
                    min-width: 200px;
                    background-color: var(--_menu-elevated-surface-color);
                    box-shadow: var(--_menu-shadow);
                    border-radius: 4px;
                    opacity: 0;
                    visibility: hidden;
                    transition: opacity 0.2s, visibility 0.2s;
                    z-index: 1001;
                    border: 1px solid var(--_menu-border-color);
                    pointer-events: none;
                    transform: translateZ(0);
                }
                
                .has-submenu:hover .submenu,
                .submenu-trigger:hover + .submenu,
                .submenu-trigger:focus + .submenu,
                .submenu:hover {
                    opacity: 1;
                    visibility: visible;
                    pointer-events: auto;
                }
                
                /* Level 2+ flyout menus */
                .flyout-menu {
                    position: absolute;
                    top: 0;
                    left: 100%;
                    min-width: 200px;
                    background-color: var(--_menu-elevated-surface-color);
                    box-shadow: var(--_menu-shadow);
                    border-radius: 4px;
                    opacity: 0;
                    visibility: hidden;
                    transition: opacity 0.2s, visibility 0.2s;
                    z-index: 1001;
                    border: 1px solid var(--_menu-border-color);
                    pointer-events: none;
                    transform: translateZ(0); /* Create stacking context for better z-index handling */
                }
                
                .has-flyout:hover .flyout-menu,
                .flyout-trigger:focus + .flyout-menu,
                .flyout-menu:hover {
                    opacity: 1;
                    visibility: visible;
                    pointer-events: auto;
                }
                .category-header {
                    padding: 10px 20px;
                    font-weight: bold;
                    color: var(--_menu-muted-text-color);
                    font-size: 0.9em;
                    text-transform: uppercase;
                    border-top: 1px solid var(--_menu-border-color);
                    margin-top: 5px;
                }
                
                .category-header:first-child {
                    border-top: none;
                    margin-top: 0;
                }
                
                .icon {
                    width: 20px;
                    height: 20px;
                    min-width: 20px;
                    margin-right: 10px;
                }
                
                /* Responsive styles */
                @media (max-width: 768px) {
                    .nav-list {
                        flex-direction: column;
                    }
                    
                    .dropdown, .flyout-menu, .submenu {
                        position: static;
                        box-shadow: none;
                        border-radius: 0;
                        width: 100%;
                        max-height: none;
                        overflow: visible;
                        border: none;
                        opacity: 1;
                        visibility: visible;
                        pointer-events: auto;
                        transition: none;
                        top: auto;
                        left: auto;
                    }
                    
                    .nav-item:hover .dropdown, 
                    .top-level-item:focus + .dropdown,
                    .dropdown:hover,
                    .has-submenu:hover .submenu,
                    .has-flyout:hover .flyout-menu,
                    .submenu-trigger:focus + .submenu,
                    .flyout-trigger:focus + .flyout-menu {
                        max-height: 1000px;
                    }
                    
                    .top-level-item {
                        padding: 12px 15px;
                    }
                    
                    /* Adjust submenus and flyout menus for mobile */
                    .submenu, .flyout-menu {
                        padding-left: 20px; /* Indent nested menus */
                    }
                }
            </style>
            ${this.createHorizontalMenu()}
        `;
        
        // Add click listener for item tracking
        this.shadowRoot.addEventListener('click', this._handleItemClick.bind(this));
        void adoptPrimeIconsStyles(this.shadowRoot);
        hydrateQdsLucideIcons(this.shadowRoot);
    }
}

// Register the component only if it's not already defined
if (!customElements.get('qds-horizontal-menu')) {
    customElements.define('qds-horizontal-menu', QdsHorizontalMenu);
}

export { QdsHorizontalMenu };
